import React, {
  ReactElement,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  useRef,
  useLayoutEffect,
} from 'react'
import { Flex, Box, useThemeUI } from 'theme-ui'
import PlansHeader from 'components/Plans/PlansHeader'
import { AlertPresenter, customDesktopScrollbarStyle } from 'ui'
import useQueryString from 'utils/useQueryString'
import PlansProgressBar from 'components/Plans/ProgressBar/ProgressBar'
import CreateAccountView from 'components/Plans/Products/CreateAccountView'
import SelectPlan from 'components/Plans/SelectPlan/SelectPlan'
import SuccessView from 'components/Plans/SuccessView'
import CheckoutView from './Products/Checkout'
import { useAppSelector } from 'store/hooks'
import { AnimatePresence, motion, MotionStyle } from 'framer-motion'
import { PlansSteps, usePlanWizardSteps } from 'components/Plans/usePlanWizardSteps'
import useMatomoForPaymentTracking from 'components/Plans/Payments/useMatomoForPaymentTracking'
import { useEntryTracking, usePlansTracking } from 'utils/campaignTracking'
import injectHTMLContent from 'utils/injectHTMLContent'
import { Global } from '@emotion/react'
import { useCheckTrialEligibilityQuery } from 'store/api/billing/payments'
import { useGetBillingProductsQuery } from 'store/api/billing/products'
import useShouldRenderOnClient from 'utils/useShouldRenderOnClient'
import { StaticPageBody, StaticPageWrapper } from '../NewStaticPage'
import { userApi } from 'store/api/user'
import { navigate } from 'gatsby'
import { noRedirectParam } from 'layouts'
import useHideBodyLoader from 'utils/useHideBodyLoader'

export enum PlanType {
  SOME = 'SOME',
  FULL = 'FULL',
}

export const stepNames = ['PLANS', 'ACCOUNT', 'FINISH']
export const stepNamesWithoutAccount = ['PLANS', 'FINISH']

export interface AccountInfoForm {
  email: string
  password: string
}

export function Plans(): ReactElement | null {
  const { qs } = useQueryString()
  const alertPresenterContainerRef = useRef<HTMLDivElement>(null)
  const { onNextClick, onPreviousClick, progressBarStepNames } = usePlanWizardSteps()
  const customHTML = useAppSelector(s => s.tracking.customHTML)
  const sessionToken = useAppSelector(s => s.persistData.sessionToken)
  const shouldRenderOnClient = useShouldRenderOnClient()
  const [accountInfoForm, setAccountInfoForm] = useState<AccountInfoForm>({
    email: '',
    password: '',
  })
  const {
    data: userData,
    isLoading: isUserDataLoading,
    isUninitialized,
  } = userApi.endpoints.getUser.useQueryState('')

  useMatomoForPaymentTracking()
  useEntryTracking()
  usePlansTracking()

  useGetBillingProductsQuery('', { skip: !sessionToken, refetchOnMountOrArgChange: true })
  useCheckTrialEligibilityQuery('', { skip: !sessionToken, refetchOnMountOrArgChange: true })

  const { theme } = useThemeUI()

  useLayoutEffect(() => {
    if (userData?.org) {
      navigate(`/personal${noRedirectParam}`)
    }
  }, [userData?.org])

  useEffect(() => {
    if (shouldRenderOnClient) {
      window['___gatsby'].scrollTop = 0
    }
  }, [qs.step, shouldRenderOnClient])

  useEffect(() => {
    customHTML && injectHTMLContent(customHTML)
  }, [customHTML])

  // this handles the state for the sign up form
  const handleAccountInfoUpdate = useCallback(
    (target): void => {
      setAccountInfoForm(prevForm => {
        return {
          ...prevForm,
          [target.name]: target.value,
        }
      })
    },
    [setAccountInfoForm],
  )

  const shouldHideLoader = sessionToken
    ? !isUninitialized && !isUserDataLoading && !userData?.org
    : !isUserDataLoading && !userData?.org

  useHideBodyLoader(shouldHideLoader)

  if (isUserDataLoading || userData?.org) {
    return null
  }

  return (
    <>
      <Global styles={customDesktopScrollbarStyle(theme)} />

      <StaticPageWrapper>
        <PlansHeader progressBarStepNames={progressBarStepNames} />
        {shouldRenderOnClient && (
          <StaticPageBody>
            <AnimatePresence exitBeforeEnter>
              {qs.step === PlansSteps.SELECT_PLAN && (
                <AnimatedStep keyName="select-plan">
                  <SelectPlan onNextClick={() => onNextClick(PlansSteps.SELECT_PLAN)} />
                </AnimatedStep>
              )}
              {qs.step === PlansSteps.CREATE_ACCOUNT && (
                <AnimatedStep
                  keyName="create-account"
                  sxContainer={{ display: 'flex', width: '100%', justifyContent: 'center' }}
                >
                  <CreateAccountView
                    form={accountInfoForm}
                    handleUpdate={handleAccountInfoUpdate}
                    onPreviousClick={onPreviousClick}
                  />
                </AnimatedStep>
              )}
              {qs.step === PlansSteps.FINISH && (
                <AnimatedStep
                  keyName="checkout"
                  sxContainer={{ display: 'flex', width: '100%', justifyContent: 'center' }}
                >
                  <CheckoutView onNextClick={() => onNextClick(PlansSteps.FINISH)} />
                </AnimatedStep>
              )}
              {qs.step === PlansSteps.SUCCESS && (
                <AnimatedStep keyName="success">
                  <SuccessView />
                </AnimatedStep>
              )}
            </AnimatePresence>
          </StaticPageBody>
        )}
        <Box
          sx={{
            display: ['flex', 'none'],
            width: ['100%', '50%'],
            height: '6.9rem',
            backgroundColor: 'blue800',
            position: 'fixed',
            bottom: 0,
            justifyContent: 'center',
            zIndex: 'zIndex50',
            borderTop: '1px solid',
            borderColor: 'blueYonder20',
          }}
        >
          <PlansProgressBar stepNames={progressBarStepNames} />
        </Box>
        <Box id="campaign-target"></Box>
      </StaticPageWrapper>
      <Flex
        sx={{
          display: 'flex',
          width: '100vw',
          height: '100%',
          position: 'fixed',
          justifyContent: 'center',
          zIndex: 'zIndex650',
          pointerEvents: 'none',
        }}
      >
        <Flex
          ref={alertPresenterContainerRef}
          sx={{
            width: '100%',
            maxWidth: ['100%', '100%', '138rem'],
            mx: ['1.6rem', '1.6rem', '4.8rem'],
            [`@media screen and (min-width: 1120px) and (max-width: 1450px)`]: {
              mx: '2.4rem',
            },
          }}
        >
          <AlertPresenter containerRef={alertPresenterContainerRef} />
        </Flex>
      </Flex>
    </>
  )
}

interface AnimatedStepProps {
  children: ReactNode
  keyName: string
  sxContainer?: MotionStyle
}

const AnimatedStep = ({ children, keyName, sxContainer }: AnimatedStepProps): ReactElement => {
  return (
    <motion.div
      key={`${keyName}`}
      initial={{ opacity: 0, x: 150 }}
      animate={{
        opacity: 1,
        x: 0,
        transition: {
          ease: 'easeInOut',
          x: { type: 'tween', duration: 0.5 },
          opacity: { duration: 0.5 },
        },
      }}
      exit={{
        opacity: 0,
        x: -150,
        transition: {
          ease: 'easeInOut',
          x: { type: 'tween', duration: 0.5 },
          opacity: { duration: 0.5 },
        },
      }}
      style={{ ...sxContainer }}
    >
      {children}
    </motion.div>
  )
}
